/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `LogoMnetInvProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface LogoMnetInvProps {
    /**
     * A string representing the CSS class to be applied to the LogoMnetInvIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#fff'
     */
    color1?: string;
    /**
     * A number or string representing the height of the LogoMnetInvIcon element.
     *
     * @default 32
     */
    height?: number | string;
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'LogoMnetInv'
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoMnetInvIcon element.
     *
     * @default 83
     */
    width?: number | string;
}

/**
 * The `LogoMnetInv` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoMnetInvProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoMnetInvIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoMnetInvIcon element.
 * @param props.ref       A ref object to access the SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoMnetInvIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoMnetInvComponent = <LogoMnetInv className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoMnetInv = ({
    className = '',
    color1 = '#fff',
    height = 40,
    ref,
    testId = 'LogoMnetInv',
    width = 103
}: LogoMnetInvProps) => (
    <svg
        ref={ref}
        aria-label="M-net Logo"
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 103 40"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M55.48 17.32c0-1.8-.07-3.3-.14-4.64h3.77l.2 2.34h.11a6.4 6.4 0 0 1 5.5-2.7c3 0 6.1 1.77 6.1 6.7v9.27h-4.34v-8.82c0-2.24-.91-3.94-3.27-3.94a3.6 3.6 0 0 0-3.39 2.3q-.23.6-.2 1.26v9.2h-4.34zm22.62 4.27c.11 2.82 2.5 4.03 5.3 4.03q2.42.04 4.75-.7l.63 2.7a17 17 0 0 1-5.99.99c-5.57 0-8.84-3.11-8.84-7.89 0-4.33 2.89-8.4 8.38-8.4 5.57 0 7.4 4.17 7.4 7.6q.01.84-.13 1.67zm7.55-2.76c.03-1.44-.67-3.82-3.56-3.82-2.68 0-3.8 2.22-3.98 3.82zM97.98 8.67v4h4.13v2.96h-4.13v6.89c0 1.92.57 2.88 2.22 2.88q.86.04 1.7-.16l.06 2.98q-1.53.45-3.13.42a5.2 5.2 0 0 1-3.74-1.28c-.95-.9-1.37-2.34-1.37-4.4v-7.33h-2.43v-2.95h2.43V9.76zm-46.5 11.81a2.7 2.7 0 0 1-1.66 2.47A2.7 2.7 0 0 1 46.16 21a2.7 2.7 0 0 1 1.14-2.74q.67-.45 1.5-.45a2.7 2.7 0 0 1 2.68 2.67" fill={color1} />
        <path d="M47.47 30.1c0 .03-1.06-1.79-1.08-1.76-2.65 3.55-5.48 4.84-6.46 4.93-1.16.1-1.83-1.3-1.93-2.3-.35-3.8 2.56-8.87 4.53-11.88l4.99-8.06c.51-.8 1.85-2.7 1.76-3.7-.08-.82-1.58-2.85-2.8-3.6q-.15-.08-.3-.13a1 1 0 0 0-.55-.04c-.87.08-3.37 2.52-3.95 3.13-5.87 5.79-9.05 9.47-14.53 15.33 1.7-3.34 7.43-12.66 7.08-16.36-.24-2.6-2.8-3.28-3.86-3.13-1.98.23-5.76 4.37-7.4 6.29l-5.21 6.28c1.23-3.18 2.01-6.4 1.55-9.1-.5-2.87-1.7-5.79-4.88-5.24-4.13.7-13 10.9-13.55 14.51l.51 1.85c5.36-7.6 8.72-10.47 10.07-10.7 1.15-.2 1.93 1.15 2.1 2.14.65 3.77-1.85 9.04-3.58 12.2L4.16 31.4c-.45.84-1.62 2.83-1.47 3.82q.15.77.6 1.43a3.3 3.3 0 0 0 2.76 1.43c1.25.02 1.85-.3 2.62-1.53l7.62-12.02 5.17-7.56 2.32-3.24s1.28-1.66 2.19-2.72c.7-.8 1.4-.51 1.48 0q0 .86-.38 1.63C25.72 16.1 24.22 19 20.23 25.6l-2.88 4.76c-.68 1.12-1.75 3.37-1.62 4.77.13 1.46 1.2 4.36 3.08 4.18 1.22-.11 3.28-3.18 3.92-4.2 6.69-10.66 8.76-12.82 16.34-20.79l.9-.9-.4.65c-2.98 4.87-8.44 12.95-7.88 18.96.27 2.9 1.63 5.96 4.83 5.67 2.78-.26 8.74-4.76 10.95-8.6" fill={color1} />
    </svg>
);

LogoMnetInv.displayName = 'LogoMnetInv';